import { ArrowRight } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useIntersectionReveal } from '@/hooks/useIntersectionReveal';
import { serviceImages } from '@/config/serviceImages';

interface ServiceCardProps {
  image: string;
  srcSet: string;
  title: string;
  description: string;
  route: string;
  index: number;
  imagePosition?: string;
  isVisible: boolean;
  badge?: string;
}

const ServiceCard = ({
  image,
  srcSet,
  title,
  description,
  route,
  index,
  imagePosition = 'center',
  isVisible,
  badge,
}: ServiceCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardRef = useRef<HTMLAnchorElement>(null);

  return (
    <Link
      ref={cardRef}
      to={route}
      className="group flex flex-col gap-6 cursor-pointer"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        transitionDelay: `${index * 100}ms`,
      }}
    >
      {/* Image Container */}
      <div className="overflow-hidden rounded-3xl aspect-[4/3] relative shadow-lg">
        <img
          src={image}
          srcSet={srcSet}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={`${title} service by Altum Contractors Houston TX`}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 saturate-[0.95] contrast-[1.05] group-hover:saturate-100 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ objectPosition: imagePosition }}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

        {/* Badge */}
        {badge && (
          <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-bold px-4 py-2 rounded-lg uppercase tracking-widest shadow-lg">
            {badge}
          </div>
        )}
      </div>

      {/* Content */}
      <div>
        <h3 className="text-2xl md:text-3xl font-heading font-bold text-secondary dark:text-white mb-3 group-hover:text-primary transition-colors">
          {title}
        </h3>
        <p className="text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
          {description}
        </p>
        <span className="inline-flex items-center text-primary font-bold uppercase text-xs tracking-[0.15em] gap-2 group-hover:gap-4 transition-all border-b border-primary/20 pb-1">
          Learn More <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
};

const ServicesSection = () => {
  const { t } = useTranslation('home');
  const { ref: sectionRef, isVisible } = useIntersectionReveal<HTMLDivElement>();

  const services = [
    {
      ...serviceImages.roofRepairs,
      title: t('services.roofRepairs.title'),
      description: t('services.roofRepairs.description'),
      route: '/services/roof-repairs',
      imagePosition: 'center 35%',
      badge: 'Residential',
    },
    {
      ...serviceImages.roofReplacement,
      title: t('services.roofReplacement.title'),
      description: t('services.roofReplacement.description'),
      route: '/services/roof-replacement',
      imagePosition: 'center',
      badge: 'Installation',
    },
    {
      ...serviceImages.roofMaintenance,
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      route: '/services/roof-maintenance',
      imagePosition: 'center',
      badge: 'Care',
    },
    {
      ...serviceImages.newConstruction,
      title: t('services.newConstruction.title'),
      description: t('services.newConstruction.description'),
      route: '/services/new-construction',
      imagePosition: 'center',
      badge: 'New Build',
    },
    {
      ...serviceImages.skylightsVentilation,
      title: t('services.enhancements.title'),
      description: t('services.enhancements.description'),
      route: '/services/skylights-ventilation',
      imagePosition: 'center',
      badge: 'Upgrades',
    },
  ];

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-32 bg-white dark:bg-slate-900">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8 border-b border-slate-100 dark:border-slate-800 pb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-2xl">
            <span className="text-primary font-extrabold tracking-[0.2em] uppercase text-xs mb-4 block">
              {t('services.eyebrow')}
            </span>
            <h2 className="text-secondary dark:text-white text-4xl md:text-5xl font-heading font-black uppercase tracking-tight">
              {t('services.headline')}
            </h2>
          </div>
          <Link
            to="/services"
            className="text-secondary dark:text-white font-bold flex items-center gap-2 hover:gap-4 transition-all px-8 py-4 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 group"
          >
            View All Services
            <ArrowRight className="w-4 h-4 group-hover:text-primary transition-colors" />
          </Link>
        </motion.div>

        {/* Services Grid - 3 columns on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {services.slice(0, 3).map((service, index) => (
            <ServiceCard
              key={service.route}
              image={service.src}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
              badge={service.badge}
            />
          ))}
        </div>

        {/* Second row - 2 cards centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12 lg:max-w-[calc(66.666%+1.5rem)] lg:mx-auto">
          {services.slice(3).map((service, index) => (
            <ServiceCard
              key={service.route}
              image={service.src}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index + 3}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
              badge={service.badge}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
